import { type FilterSummary, type FilterSummaryItem } from '../types/filters';
import type { TaxonomyTerm } from '@skin-seoul/service';

/**
 * Normalize a string for brand name comparison
 * Removes special characters, spaces, and converts to lowercase
 */
const normalizeBrandString = (str: string): string => {
  return str
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '') // Remove all non-alphanumeric characters
    .trim();
};

/**
 * Find brand term ID from filter summary by brand slug
 * Dynamically matches brand slugs to brand names in filterSummary without hardcoded mappings
 *
 * @param brandSlug - The brand slug from URL (e.g., "cosrx", "beauty-of-joseon")
 * @param filterSummary - The filter summary containing available brands with termIDs
 * @returns The brand termID if found, undefined otherwise
 */
export const findBrandTermIdFromSummary = (brandSlug: string, filterSummary?: FilterSummary): number | undefined => {
  if (!filterSummary?.brand || !brandSlug) {
    return undefined;
  }

  // Clean the brand slug from URL (remove leading/trailing slashes, etc.)
  const cleanSlug = brandSlug.toLowerCase().replace(/[^a-z0-9-]/g, '');
  const normalizedSlug = normalizeBrandString(cleanSlug);

  // Try to find a brand in filterSummary that matches the slug
  const matchedBrand = filterSummary.brand.find((item: FilterSummaryItem) => {
    const normalizedBrandName = normalizeBrandString(item.name);

    // Direct match: normalized brand name equals normalized slug
    if (normalizedBrandName === normalizedSlug) {
      return true;
    }

    // Handle common URL slug patterns:
    // "dr-jart" should match "Dr. Jart+"
    // "some-by-mi" should match "SOME BY MI"
    // "beauty-of-joseon" should match "Beauty of Joseon"
    const slugWithSpaces = cleanSlug.replace(/-/g, '');
    if (normalizedBrandName === slugWithSpaces) {
      return true;
    }

    return false;
  });

  if (matchedBrand) {
    return matchedBrand.termId;
  }

  return undefined;
};

/**
 * Find brand term ID from brand taxonomy by brand slug
 * This is optimized for initial page load without needing filterSummary
 *
 * @param brandSlug - The brand slug from URL (e.g., "cosrx", "beauty-of-joseon")
 * @param brandTaxonomy - The brand taxonomy data with termIDs and slugs
 * @returns The brand termID if found, undefined otherwise
 */
export const findBrandTermIdFromTaxonomy = (brandSlug: string, brandTaxonomy?: TaxonomyTerm[]): number | undefined => {
  if (!brandTaxonomy || !brandSlug) {
    return undefined;
  }

  // Clean the brand slug from URL (remove leading/trailing slashes, etc.)
  const cleanSlug = brandSlug.toLowerCase().replace(/[^a-z0-9-]/g, '');
  const normalizedSlug = normalizeBrandString(cleanSlug);

  // Try to find a brand in taxonomy that matches the slug
  const matchedBrand = brandTaxonomy.find((term: TaxonomyTerm) => {
    // Direct slug match (preferred)
    if (term.slug === cleanSlug) {
      return true;
    }

    // Normalize name for flexible matching
    const normalizedBrandName = normalizeBrandString(term.name);

    // Direct match: normalized brand name equals normalized slug
    if (normalizedBrandName === normalizedSlug) {
      return true;
    }

    // Handle common URL slug patterns:
    // "dr-jart" should match "Dr. Jart+"
    // "some-by-mi" should match "SOME BY MI"
    // "beauty-of-joseon" should match "Beauty of Joseon"
    const slugWithSpaces = cleanSlug.replace(/-/g, '');
    if (normalizedBrandName === slugWithSpaces) {
      return true;
    }

    return false;
  });

  if (matchedBrand) {
    return matchedBrand.termId;
  }

  return undefined;
};
